import { makeStyles } from '@mui/styles';
import { GridColDef, GridRowParams } from '@mui/x-data-grid';
import { format, isValid } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import enLocale from 'date-fns/locale/en-US';
import frLocale from 'date-fns/locale/fr-CA';
import useTimezone from 'hooks/useTimezone';
import useUser from 'hooks/useUser';
import moment from 'moment-timezone';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import BasicTable from 'views/components/BasicTable';
import LinkViewButton from 'views/components/buttons/LinkViewButton';

const useStyles = makeStyles(() => ({
  Incomplete: {
    backgroundColor: '#f2dede',
    '&:hover': {
      backgroundColor: '#ebcccc !important'
    }
  },
  Completed: {
    backgroundColor: 'inherit'
  }
}));

interface Props {
  userId: string;
  informedConsents: any;
}

interface ClientFormRow {
  id: 'informed-consent';
  version: string;
  acceptedOn: string;
  status: string;
  therapist: string;
  therapistId?: string;
}

export default function DocumentInformedConsent({ userId, informedConsents }: Props) {
  const { user } = useUser(userId);
  const intl = useIntl();
  const classes = useStyles();
  const { timezone } = useTimezone();

  const columns: GridColDef[] = [
    {
      field: 'version',
      headerName: intl.formatMessage({ defaultMessage: 'IC Version' }),
      minWidth: 160,
      renderCell: (params) => {
        const row = params.row as ClientFormRow;
        return row.version;
      }
    },
    {
      field: 'therapist',
      headerName: intl.formatMessage({ defaultMessage: 'Provider' }),
      minWidth: 150,
      renderCell: (params) => {
        const row = params.row as ClientFormRow;
        return <strong>{row.therapist}</strong>;
      }
    },
    {
      field: 'acceptedOn',
      headerName: intl.formatMessage({ defaultMessage: 'Accepted On' }),
      width: 300,
      renderCell: (params) => {
        const row = params.row as ClientFormRow;

        const lastUpdateMoment = new Date(row.acceptedOn);

        return isValid(lastUpdateMoment)
          ? format(utcToZonedTime(lastUpdateMoment, timezone), intl.locale === 'fr' ? 'do MMMM yyyy, HH:mm' : 'MMMM do yyyy, h:mm a', {
              locale: intl.locale === 'fr' ? frLocale : enLocale
            })
          : '-';
      }
    },
    {
      field: 'status',
      headerName: intl.formatMessage({ defaultMessage: 'Status' }),
      minWidth: 150,
      renderCell: (params) => {
        const row = params.row as ClientFormRow;
        return <strong>{row.status === '-' ? 'Provider Updated' : row.status}</strong>;
      }
    },
    {
      field: '',
      width: 340,
      renderCell: (params) => {
        const row = params.row as ClientFormRow;

        if (user) {
          return row.acceptedOn !== '-' ? (
            <LinkViewButton getLinkUrl={`/therapists/${row.therapistId}/patients/${userId}/informed-consent-history/${row.id}`} />
          ) : (
            '-'
          );
        }

        return null;
      }
    }
  ];

  const rows = useMemo(() => {
    if (!user) {
      return null;
    }

    // only add the intake if enabled
    const result: ClientFormRow[] = [];

    informedConsents?.forEach((informedConsent) => {
      result.push({
        id: informedConsent._id,
        version: String(informedConsent.version),
        acceptedOn: informedConsent.acceptedOn ? moment(informedConsent.acceptedOn).format('LLLL z') : '-',
        status: informedConsent.acceptedOn ? 'Accepted' : '-',
        therapist: informedConsent.therapist,
        therapistId: informedConsent.therapistId ?? user.therapist
      });
    });

    return result;
  }, [user, informedConsents]);

  return (
    <BasicTable
      emptyMessage={<FormattedMessage defaultMessage="No informed consent found." />}
      columns={columns}
      rows={rows}
      getRowClassName={(params: GridRowParams) => classes[params?.row?.status]}
      paginationMode="client"
      pageSize={5}
      hideSearch
      sx={{
        backgroundColor: '#e3f2fd'
      }}
    />
  );
}
